/**
 * Path: src/features/browser/composables/useEntitySchema.ts
 * Task: Composable to generate and filter the dynamic form schema for the active entity.
 * Update: Cross-references global relations with project-level active entity types to hide dead-end relations.
 * Fix: Inverse relations configured globally as Pill Buttons are forced to render as multiselect lists in the form body.
 */
import { computed, type Ref } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import type { Entity } from '@/core/types/editor';

function getRelationPropKey(systemKey: string, isMultiple: boolean): string {
  if (['cairo', 'predecessors'].includes(systemKey)) return systemKey;
  return isMultiple ? `${systemKey}Ids` : `${systemKey}Id`;
}

function getInversePropKey(relationId: string, isMultiple: boolean = true): string {
  return isMultiple ? `inverse_${relationId}Ids` : `inverse_${relationId}Id`;
}

function capitalizeFirst(str?: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function useEntitySchema(activeEntity: Ref<Entity | null>, isLocked: Ref<boolean>) {
  const authStore = useAuthStore();
  const projectStore = useProjectStore();

  const combinedSchema = computed(() => {
    if (!activeEntity.value) return [];
    const appData = authStore.appData;
    if (!appData) return [];

    const entityDef = appData.entities?.find((e: any) => e.name === activeEntity.value?.entityType);
    if (!entityDef) return [];

    // Project active types filter
    const activeProjectTypes = projectStore.loadedProject?.value
      ? (projectStore.projectsMetadata[projectStore.loadedProject.value]?.entityTypes || [])
      : [];

    // 1. Fields (Standard Attributes)
    const fields = (entityDef.fields || []).map((f: any) => ({ ...f, _kind: 'field' }));

    // 2. Outgoing Relations (Connections)
    const relations = (appData.relations || [])
      .filter((r: any) => {
        if (!r.sourceTypes?.includes(entityDef.name)) return false;

        // Hide relation if none of its target types are active in the project
        if (activeProjectTypes.length > 0 && r.targetTypes) {
          const hasActiveTarget = r.targetTypes.some((t: string) => activeProjectTypes.includes(t));
          if (!hasActiveTarget) return false;
        }
        return true;
      })
      .map((r: any) => {
        // Robust Fallback: If DB hasn't been migrated yet, default to multiselect, except for phases (timeline)
        const resolvedUi = r.uiPresentation || (r.id === 'phases' ? 'timeline' : 'multiselect');

        // Filter target info to only show active types in the tooltip
        const activeTargets = activeProjectTypes.length > 0
          ? r.targetTypes?.filter((t: string) => activeProjectTypes.includes(t))
          : r.targetTypes;

        return {
          _kind: 'relation',
          id: r.id,
          name: capitalizeFirst(r.labelSourceTarget || r.name),
          isMultiple: r.isMultiple,
          section: r.sectionId || 'connections',
          sectionRank: r.sectionRank || 999,
          uiPresentation: resolvedUi,
          asField: {
            id: r.id === 'predecessors' ? 'predecessors' : (r.id === 'cairo' ? 'cairo' : getRelationPropKey(r.id, r.isMultiple)),
            name: capitalizeFirst(r.labelSourceTarget || r.name),
            info: `Target: ${activeTargets?.map((t: string) => capitalizeFirst(t)).join(', ') || 'Unknown'}`,
            infoGer: r.labelSourceTargetGer,
            entitySource: r.id,
            type: 'relation'
          }
        };
      });

    // 3. Incoming Relations (Incoming Links)
    const inverseRelations: any[] = [];
    (appData.relations || []).forEach((r: any) => {
      if (r.targetTypes?.includes(entityDef.name)) {
        // Hide relation if none of its source types are active in the project
        if (activeProjectTypes.length > 0 && r.sourceTypes) {
          const hasActiveSource = r.sourceTypes.some((t: string) => activeProjectTypes.includes(t));
          if (!hasActiveSource) return;
        }

        const resolvedUi = r.uiPresentation || (r.id === 'phases' ? 'timeline' : 'multiselect');
        const activeSources = activeProjectTypes.length > 0
          ? r.sourceTypes?.filter((t: string) => activeProjectTypes.includes(t))
          : r.sourceTypes;

        // Mindmap handles both directions. Render it only if the entity is not already rendering it as a source.
        if (resolvedUi === 'mindmap') {
          if (!r.sourceTypes?.includes(entityDef.name)) {
            inverseRelations.push({
              _kind: 'relation', // Push as 'relation' to force primary component rendering
              id: r.id,
              name: capitalizeFirst(r.name),
              isMultiple: r.isMultiple,
              section: r.sectionId || 'connections',
              sectionRank: r.sectionRank || 999,
              uiPresentation: resolvedUi,
              asField: {
                id: r.id === 'predecessors' ? 'predecessors' : getRelationPropKey(r.id, r.isMultiple),
                name: capitalizeFirst(r.name),
                type: 'relation',
                entitySource: r.id
              }
            });
          }
          return;
        }

        // Matrix modules (like CAIRO) handle bidirectional displays implicitly
        if (resolvedUi === 'matrix') {
          inverseRelations.push({
            _kind: 'inverse_relation',
            id: `inverse_${r.id}`,
            name: capitalizeFirst(r.name),
            section: r.sectionId || 'connections',
            sectionRank: r.sectionRank || 999,
            uiPresentation: resolvedUi,
            asField: {
              id: `inverse_${r.id}`,
              name: capitalizeFirst(r.name),
              type: 'inverse_relation',
              entitySource: r.sourceTypes?.[0] || 'unknown'
            },
            tableColumns: []
          });
          return;
        }

        // ADR-001: Pills sind Header-Aktionen. In der Inversen Richtung (Target -> Source)
        // sollen die zugewiesenen Quell-Entitäten als normale Liste im Formular auftauchen.
        let finalInverseUi = resolvedUi;
        if (finalInverseUi === 'pill_button') {
          finalInverseUi = 'multiselect';
        }

        // Standard Generic Inverse Relation
        inverseRelations.push({
          _kind: 'inverse_relation',
          id: `inverse_${r.id}`,
          name: capitalizeFirst(r.labelTargetSource || `Incoming from ${r.name}`),
          section: r.sectionId || 'connections',
          sectionRank: r.sectionRank || 999,
          uiPresentation: finalInverseUi,
          asField: {
            id: getInversePropKey(r.id, true),
            name: capitalizeFirst(r.labelTargetSource || `Incoming from ${r.name}`),
            info: `Source: ${activeSources?.map((t: string) => capitalizeFirst(t)).join(', ') || 'Unknown'}`,
            type: 'inverse_relation',
            entitySource: r.id
          },
          tableColumns: []
        });
      }
    });

    // Sort by configured rank
    return [...fields, ...relations, ...inverseRelations].sort((a: any, b: any) => {
      const rankA = a.sectionRank ?? a.formRank ?? 999;
      const rankB = b.sectionRank ?? b.formRank ?? 999;
      return rankA - rankB;
    });
  });

  const filteredCombinedSchema = computed(() => {
    const schema = combinedSchema.value;
    if (!schema || !activeEntity.value) return [];

    const entitySystemValue = activeEntity.value.system;

    return schema.filter((item: any) => {
      // --- FIELDS FILTER ---
      if (item._kind === 'field') {
        if (item.id === 'lastUpdated' || item.type === 'version') return false;
        if (item.id === 'title') return false; // Managed exclusively by the header

        if (isLocked.value) {
          if (['id', 'levelId', 'areaId'].includes(item.id)) return false;
          if (item.type === 'label' && ['Connected Artifacts', 'Helpers'].includes(item.name)) return false;
        }

        if (entitySystemValue && item.invisible?.includes(entitySystemValue)) return false;
        return true;
      }

      // --- RELATIONS FILTER ---
      else if (item._kind === 'relation' || item._kind === 'inverse_relation') {
        // ALWAYS hide pill buttons from the main form content body, as they are exclusively rendered in the EntityHeader!
        // CRITICAL FIX: Only hide forward relations. Inverse relations are forced to render as standard multiselect lists in the body.
        if (item.uiPresentation === 'pill_button' && item._kind === 'relation') {
          return false;
        }
        return true;
      }

      return true;
    });
  });

  return {
    combinedSchema,
    filteredCombinedSchema
  };
}
